import { type Dispatch, type ReactNode } from 'react';
import SingleSelection from '../components/SingleSelection';
import { chartMetricShowLegendBadge, SelectedOthers } from '../constants';
import type { AxisType, ChartAxisChoice, ChartCardStateAction, ChartChoice, ChartDimensionChoice, ChartDimensionValueChoice, ChartDimensionValueOfChoice, ChartDimensionValuesChoice, ChartDimensionValuesOfChoice, ChartMetricChoice, ChartMetricsChoice, ChartMetricTransposedChoice, ChartMetricVariantsChoice, ChartVariantValueChoice, ChartVariantValuesChoice } from '../internalTypes';
import type { ChartCardTFunction, ChartDataRow, ChartSelections, SelectionValue } from '../types';
import { assertNever, errorLocal, generateIdForSelectionOthers, getValueFromTransposedMetricColumn } from './generics';
import MultipleSelection from '../components/MultipleSelection';
import { changeIsLocked } from './changeVisibility';
const generateVariantValueChartChoiceElement = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  choicesMap,
  generatedChoices,
  selections,
  showBadge,
  singleElements,
  dispatch
}: {
  choice: ChartMetricChoice<DataKey, DataRow> | ChartMetricsChoice<DataKey, DataRow> | ChartMetricTransposedChoice<DataKey, DataRow>;
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>;
  generatedChoices: Set<DataKey>;
  selections: ChartSelections<DataKey>;
  showBadge?: boolean;
  singleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
}): boolean => {
  const {
    id: choiceId,
    type,
    variant
  } = choice;
  if (variant == null) {
    return false;
  }
  const variantValueChoice = choicesMap[choice.variant] as ChartVariantValueChoice<DataKey, DataRow> | ChartVariantValuesChoice<DataKey, DataRow>;
  if (variantValueChoice.type === 'variantValues') {
    return false;
  }
  const selectedVariantValue = selections[variantValueChoice.id];
  if (typeof selectedVariantValue !== 'string') {
    errorLocal(`generateVariantValueChartChoiceElement ${choiceId} ${type} invalid selectedVariantValue`, {
      choice,
      variantValueChoice,
      selectedVariantValue
    });
    return true;
  }
  const invalidSelectedVariantValue = !variantValueChoice.change.options.some(({
    value
  }) => value === selectedVariantValue);
  if (invalidSelectedVariantValue) {
    errorLocal(`generateVariantValueChartChoiceElement ${choiceId} ${type} invalid selectedVariantValue`, {
      variantValueChoice,
      selectedVariantValue
    });
    return true;
  }
  singleElements.push(<SingleSelection<DataKey, DataRow> key={variantValueChoice.id} type={variantValueChoice.type} choice={variantValueChoice} selected={selectedVariantValue} showBadge={showBadge} dispatch={dispatch} locked={changeIsLocked(variantValueChoice) || generatedChoices.has(variantValueChoice.id)} />);
  generatedChoices.add(variantValueChoice.id);
  return false;
};
const generateChoiceMetricChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  choicesMap,
  generatedChoices,
  selections,
  selected,
  singleElements,
  dispatch
}: {
  choice: ChartMetricChoice<DataKey, DataRow>;
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>;
  generatedChoices: Set<DataKey>;
  selections: ChartSelections<DataKey>;
  selected: SelectionValue<DataKey> | undefined;
  singleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
}): void => {
  const {
    id: choiceId,
    type,
    chartType
  } = choice;
  if (typeof selected !== 'string') {
    errorLocal(`generateChoiceMetricChartChoiceElements ${choiceId} ${type} invalid selected`, {
      choice,
      selected
    });
    return;
  }
  if (generateVariantValueChartChoiceElement<DataKey, DataRow>({
    choice,
    choicesMap,
    generatedChoices,
    selections,
    showBadge: chartMetricShowLegendBadge[chartType],
    singleElements,
    dispatch
  })) {
    return;
  }
  singleElements.push(<SingleSelection<DataKey, DataRow> key={choiceId} type={type} choice={choice} selected={selected} dispatch={dispatch} locked={changeIsLocked(choice) || generatedChoices.has(choiceId)} showBadge={chartMetricShowLegendBadge[chartType]} />);
  generatedChoices.add(choiceId);
};
const generateChoiceMetricsChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  useChips,
  choice,
  choicesMap,
  generatedChoices,
  selections,
  selected,
  singleElements,
  multipleElements,
  dispatch,
  t
}: {
  useChips: boolean;
  choice: ChartMetricsChoice<DataKey, DataRow>;
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>;
  generatedChoices: Set<DataKey>;
  selections: ChartSelections<DataKey>;
  selected: SelectionValue<DataKey> | undefined;
  singleElements: ReactNode[];
  multipleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
}): void => {
  const {
    id: choiceId,
    type
  } = choice;
  if (!Array.isArray(selected)) {
    errorLocal(`generateChoiceMetricsChartChoiceElements ${choiceId} ${type} invalid selected`, {
      choice,
      selected
    });
    return;
  }
  if (generateVariantValueChartChoiceElement<DataKey, DataRow>({
    choice,
    choicesMap,
    generatedChoices,
    selections,
    singleElements,
    dispatch
  })) {
    return;
  }
  multipleElements.push(<MultipleSelection<DataKey, DataRow> key={choiceId} type={type} choice={choice} useChips={useChips} selected={selected} dispatch={dispatch} locked={changeIsLocked(choice) || generatedChoices.has(choiceId)} t={t} />);
  generatedChoices.add(choiceId);
};
const generateChoiceMetricTransposedValuesChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  useChips,
  choice,
  dimensionValuesChoice,
  generatedChoices,
  selectedDimension,
  metrics,
  selections,
  multipleElements,
  dispatch,
  t
}: {
  useChips: boolean;
  choice: ChartChoice<DataKey, DataRow>;
  dimensionValuesChoice: ChartDimensionValuesChoice<DataKey, DataRow> | ChartDimensionValuesOfChoice<DataKey, DataRow>;
  generatedChoices: Set<DataKey>;
  selectedDimension: DataKey | undefined;
  metrics: Set<DataKey> | undefined;
  selections: ChartSelections<DataKey>;
  multipleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
}): void => {
  const {
    id: valuesChoiceId
  } = dimensionValuesChoice;
  let selectedDimensionValues = selections[valuesChoiceId] as string[];
  const selectedOthers = selections[generateIdForSelectionOthers(valuesChoiceId)] === SelectedOthers;
  if (!Array.isArray(selectedDimensionValues)) {
    errorLocal(`generateChoiceMetricTransposedValuesChartChoiceElements ${choice.id} ${choice.type} selectedDimensionValues invalid`, {
      choice,
      dimensionValuesChoice,
      selectedDimensionValues
    });
    return;
  }
  const sortedValues: string[] | undefined = metrics == null ? undefined : Array.from(metrics).map(metric => getValueFromTransposedMetricColumn(metric));
  if (sortedValues != null) {
    selectedDimensionValues = sortedValues.filter(value => selectedDimensionValues.includes(value));
  }
  if (dimensionValuesChoice.type === 'values') {
    multipleElements.push(<MultipleSelection<DataKey, DataRow> key={dimensionValuesChoice.id} type={dimensionValuesChoice.type} choice={dimensionValuesChoice} parentChoice={choice} useChips={useChips} sortedValues={sortedValues} selected={selectedDimensionValues} selectedOthers={selectedOthers} dispatch={dispatch} locked={changeIsLocked(dimensionValuesChoice) || generatedChoices.has(dimensionValuesChoice.id)} t={t} />);
  } else {
    multipleElements.push(<MultipleSelection<DataKey, DataRow> key={dimensionValuesChoice.id} type={dimensionValuesChoice.type} choice={dimensionValuesChoice} parentChoice={choice} useChips={useChips} sortedValues={sortedValues} selected={selectedDimensionValues} selectedOthers={selectedOthers} otherSelected={selectedDimension as string} dispatch={dispatch} locked={changeIsLocked(dimensionValuesChoice) || generatedChoices.has(dimensionValuesChoice.id)} t={t} />);
  }
  generatedChoices.add(dimensionValuesChoice.id);
};
const generateChoiceMetricTransposedChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  useChips,
  choice,
  choicesMap,
  generatedChoices,
  transposedMetricsLists,
  selections,
  selected,
  singleElements,
  multipleElements,
  dispatch,
  t
}: {
  useChips: boolean;
  choice: ChartMetricTransposedChoice<DataKey, DataRow>;
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>;
  generatedChoices: Set<DataKey>;
  transposedMetricsLists: Map<DataKey, Set<DataKey>>;
  selections: ChartSelections<DataKey>;
  selected: SelectionValue<DataKey> | undefined;
  singleElements: ReactNode[];
  multipleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
}): void => {
  const {
    id: choiceId,
    type
  } = choice;
  if (typeof selected !== 'string') {
    errorLocal(`generateChoiceMetricTransposedChartChoiceElements ${choiceId} ${type} invalid selected`, {
      choice,
      selected
    });
    return;
  }
  singleElements.push(<SingleSelection<DataKey, DataRow> key={choiceId} type={type} choice={choice} selected={selected} dispatch={dispatch} locked={changeIsLocked(choice)} />);
  generatedChoices.add(choiceId);
  if (generateVariantValueChartChoiceElement<DataKey, DataRow>({
    choice,
    choicesMap,
    generatedChoices,
    selections,
    singleElements,
    dispatch
  })) {
    return;
  }
  const dimensionValuesChoice = choicesMap[choice.transpose] as ChartDimensionValuesChoice<DataKey, DataRow> | ChartDimensionValuesOfChoice<DataKey, DataRow> | undefined;
  if (dimensionValuesChoice == null) {
    return;
  }
  const dimensionChoice = (dimensionValuesChoice.dimension === choice.transpose ? undefined : choicesMap[dimensionValuesChoice.dimension]) as ChartDimensionChoice<DataKey, DataRow> | undefined;
  if (dimensionValuesChoice.type === 'values') {
    if (dimensionChoice != null) {
      errorLocal(`generateChoiceMetricTransposedChartChoiceElements ${choiceId} ${type} invalid dimensionValuesChoice`, {
        choice,
        dimensionValuesChoice,
        dimensionChoice
      });
      return;
    }
  }
  if (dimensionChoice != null && dimensionChoice.type !== 'dimension') {
    errorLocal(`generateChoiceMetricTransposedChartChoiceElements ${choiceId} ${type} invalid dimensionChoice`, {
      choice,
      dimensionChoice
    });
    return;
  }
  let selectedDimension: DataKey | undefined;
  if (dimensionChoice != null) {
    selectedDimension = selections[dimensionChoice.id] as DataKey;
    if (typeof selectedDimension !== 'string') {
      errorLocal(`generateChoiceMetricTransposedChartChoiceElements ${choiceId} ${type} invalid selectedDimension`, {
        choice,
        dimensionChoice,
        selectedDimension
      });
      return;
    }
    singleElements.push(<SingleSelection<DataKey, DataRow> key={dimensionChoice.id} type={dimensionChoice.type} choice={dimensionChoice} selected={selectedDimension} dispatch={dispatch} locked={changeIsLocked(dimensionChoice) || generatedChoices.has(dimensionChoice.id)} />);
    generatedChoices.add(dimensionChoice.id);
  }
  generateChoiceMetricTransposedValuesChartChoiceElements({
    useChips,
    choice,
    dimensionValuesChoice,
    generatedChoices,
    selectedDimension,
    metrics: transposedMetricsLists.get(choiceId),
    selections,
    multipleElements,
    dispatch,
    t
  });
};
const generateDimensionChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  dimensionChoice,
  generatedChoices,
  selections,
  singleElements,
  dispatch
}: {
  choice: ChartChoice<DataKey, DataRow>;
  dimensionChoice: ChartDimensionChoice<DataKey, DataRow>;
  generatedChoices: Set<DataKey>;
  selections: ChartSelections<DataKey>;
  singleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
}): void => {
  const selectedDimension = selections[dimensionChoice.id] as DataKey;
  if (typeof selectedDimension !== 'string') {
    errorLocal(`generateDimensionChartChoiceElements ${choice.id} ${choice.type} invalid selectedDimension`, {
      choice,
      dimensionChoice,
      selectedDimension
    });
    return;
  }
  singleElements.push(<SingleSelection<DataKey, DataRow> key={dimensionChoice.id} type={dimensionChoice.type} choice={dimensionChoice} selected={selectedDimension} dispatch={dispatch} locked={changeIsLocked(dimensionChoice) || generatedChoices.has(dimensionChoice.id)} />);
  generatedChoices.add(dimensionChoice.id);
};
const geberateDimensionValueChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  dimensionValueChoice,
  generatedChoices,
  selections,
  singleElements,
  dispatch
}: {
  choice: ChartChoice<DataKey, DataRow>;
  dimensionValueChoice: ChartDimensionValueChoice<DataKey, DataRow> | ChartDimensionValueOfChoice<DataKey, DataRow>;
  generatedChoices: Set<DataKey>;
  selections: ChartSelections<DataKey>;
  singleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
}): void => {
  const {
    id: valueChoiceId,
    dimension
  } = dimensionValueChoice;
  const selectedDimensionValue = selections[valueChoiceId] as string;
  if (typeof selectedDimensionValue !== 'string') {
    errorLocal(`geberateDimensionValueChartChoiceElements ${choice.id} ${choice.type} invalid selectedDimensionValue`, {
      choice,
      dimensionValueChoice,
      selectedDimensionValue
    });
    return;
  }
  if (dimensionValueChoice.type === 'value') {
    singleElements.push(<SingleSelection<DataKey, DataRow> key={dimensionValueChoice.id} type={dimensionValueChoice.type} choice={dimensionValueChoice} selected={selectedDimensionValue} dispatch={dispatch} locked={changeIsLocked(dimensionValueChoice) || generatedChoices.has(dimensionValueChoice.id)} />);
  } else {
    const selectedDimension = selections[dimension] as DataKey;
    if (typeof selectedDimension !== 'string') {
      errorLocal(`geberateDimensionValueChartChoiceElements ${choice.id} ${choice.type} invalid selectedDimension`, {
        choice,
        dimensionValueChoice,
        selectedDimension
      });
      return;
    }
    singleElements.push(<SingleSelection<DataKey, DataRow> key={dimensionValueChoice.id} type={dimensionValueChoice.type} choice={dimensionValueChoice} selected={selectedDimensionValue} otherSelected={selectedDimension as string} dispatch={dispatch} locked={changeIsLocked(dimensionValueChoice) || generatedChoices.has(dimensionValueChoice.id)} />);
  }
  generatedChoices.add(dimensionValueChoice.id);
};
const generateChoiceMetricVariantsTransposedChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  choicesMap,
  generatedChoices,
  selections,
  singleElements,
  dispatch
}: {
  choice: ChartMetricVariantsChoice<DataKey, DataRow>;
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>;
  generatedChoices: Set<DataKey>;
  selections: ChartSelections<DataKey>;
  singleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
}): void => {
  const {
    id: choiceId,
    type,
    transpose
  } = choice;
  const dimensionValueChoice = choicesMap[transpose] as ChartDimensionValueChoice<DataKey, DataRow> | ChartDimensionValueOfChoice<DataKey, DataRow> | undefined;
  if (dimensionValueChoice == null) {
    return;
  }
  const dimensionChoice = (dimensionValueChoice.dimension === transpose ? undefined : choicesMap[dimensionValueChoice.dimension]) as ChartDimensionChoice<DataKey, DataRow> | undefined;
  if (dimensionChoice != null) {
    if (dimensionValueChoice.type === 'value') {
      errorLocal(`generateChoiceMetricVariantsTransposedChartChoiceElements ${choiceId} ${type} invalid dimensionValueChoice`, {
        choice,
        dimensionValueChoice,
        dimensionChoice
      });
      return;
    }
    if (dimensionChoice.type !== 'dimension') {
      errorLocal(`generateChoiceMetricVariantsTransposedChartChoiceElements ${choiceId} ${type} invalid dimensionChoice`, {
        choice,
        dimensionChoice
      });
      return;
    }
    generateDimensionChartChoiceElements({
      choice,
      dimensionChoice,
      generatedChoices,
      selections,
      singleElements,
      dispatch
    });
  }
  if (dimensionValueChoice != null) {
    geberateDimensionValueChartChoiceElements({
      choice,
      dimensionValueChoice,
      generatedChoices,
      selections,
      singleElements,
      dispatch
    });
  }
};
const generateChoiceMetricVariantsChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  useChips,
  choice,
  choicesMap,
  generatedChoices,
  selections,
  selected,
  singleElements,
  multipleElements,
  dispatch,
  t
}: {
  useChips: boolean;
  choice: ChartMetricVariantsChoice<DataKey, DataRow>;
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>;
  generatedChoices: Set<DataKey>;
  selections: ChartSelections<DataKey>;
  selected: SelectionValue<DataKey> | undefined;
  singleElements: ReactNode[];
  multipleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
}): void => {
  const {
    id: choiceId,
    type
  } = choice;
  if (typeof selected !== 'string') {
    errorLocal(`generateChoiceMetricVariantsChartChoiceElements ${choiceId} ${type} invalid selected`, {
      choice,
      selected
    });
    return;
  }
  if (choice.transpose != null) {
    generateChoiceMetricVariantsTransposedChartChoiceElements({
      choice,
      choicesMap,
      generatedChoices,
      selections,
      singleElements,
      dispatch
    });
  }
  singleElements.push(<SingleSelection<DataKey, DataRow> key={choiceId} type={type} choice={choice} selected={selected} dispatch={dispatch} locked={changeIsLocked(choice) || generatedChoices.has(choiceId)} />);
  generatedChoices.add(choiceId);
  const variantValuesChoice = choice.variant == null ? undefined : choicesMap[choice.variant];
  if (variantValuesChoice?.type !== 'variantValues') {
    errorLocal(`generateChoiceMetricVariantsChartChoiceElements ${choiceId} ${type} invalid variantValuesChoice`, {
      choice,
      variantValuesChoice
    });
    return;
  }
  const variantValues = selections[variantValuesChoice.id];
  if (!Array.isArray(variantValues)) {
    errorLocal(`generateChoiceMetricVariantsChartChoiceElements ${choiceId} ${type} invalid variantValues`, {
      choice,
      variantValuesChoice,
      variantValues
    });
    return;
  }
  multipleElements.push(<MultipleSelection<DataKey, DataRow> key={variantValuesChoice.id} type={variantValuesChoice.type} choice={variantValuesChoice} parentChoice={choice} useChips={useChips} selected={variantValues} dispatch={dispatch} locked={changeIsLocked(variantValuesChoice) || generatedChoices.has(variantValuesChoice.id)} t={t} />);
  generatedChoices.add(variantValuesChoice.id);
};
const generateChoiceValuesChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  useChips,
  choice,
  generatedChoices,
  selected,
  multipleElements,
  dispatch,
  t
}: {
  useChips: boolean;
  choice: ChartDimensionValuesChoice<DataKey, DataRow>;
  generatedChoices: Set<DataKey>;
  selected: SelectionValue<DataKey> | undefined;
  multipleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
}): void => {
  const {
    id: choiceId,
    type
  } = choice;
  if (!Array.isArray(selected)) {
    errorLocal(`generateChoiceValuesChartChoiceElements ${choiceId} ${type} invalid selected`, {
      choice,
      selected
    });
    return;
  }
  multipleElements.push(<MultipleSelection<DataKey, DataRow> key={choiceId} type={type} choice={choice} useChips={useChips} selected={selected} selectedOthers={false} dispatch={dispatch} locked={changeIsLocked(choice) || generatedChoices.has(choiceId)} t={t} />);
  generatedChoices.add(choiceId);
};
const generateChoiceValuesOfChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  useChips,
  choice,
  choicesMap,
  generatedChoices,
  selections,
  selected,
  singleElements,
  multipleElements,
  dispatch,
  t
}: {
  useChips: boolean;
  choice: ChartDimensionValuesOfChoice<DataKey, DataRow>;
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>;
  generatedChoices: Set<DataKey>;
  selections: ChartSelections<DataKey>;
  selected: SelectionValue<DataKey> | undefined;
  singleElements: ReactNode[];
  multipleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
}): void => {
  const {
    id: choiceId,
    type,
    dimension
  } = choice;
  if (!Array.isArray(selected)) {
    errorLocal(`generateChoiceValuesOfChartChoiceElements ${choiceId} ${type} invalid selected`, {
      choice,
      selected
    });
    return;
  }
  const dimensionChoice = dimension == null ? undefined : choicesMap[dimension];
  if (dimensionChoice?.type !== 'dimension') {
    errorLocal(`generateChoiceValuesOfChartChoiceElements ${choiceId} ${type} invalid dimensionChoice`, {
      choice,
      dimensionChoice
    });
    return;
  }
  const selectedDimension = selections[dimensionChoice.id];
  if (typeof selectedDimension !== 'string') {
    errorLocal(`generateChoiceValuesOfChartChoiceElements ${choiceId} ${type} invalid selectedDimension`, {
      choice,
      dimensionChoice,
      selectedDimension
    });
    return;
  }
  singleElements.push(<SingleSelection<DataKey, DataRow> key={dimensionChoice.id} type={dimensionChoice.type} choice={dimensionChoice} selected={selectedDimension} dispatch={dispatch} locked={changeIsLocked(dimensionChoice) || generatedChoices.has(dimensionChoice.id)} />);
  generatedChoices.add(dimensionChoice.id);
  multipleElements.push(<MultipleSelection<DataKey, DataRow> key={choiceId} type={type} choice={choice} useChips={useChips} selected={selected} selectedOthers={selections[generateIdForSelectionOthers(choiceId)] === SelectedOthers} otherSelected={selectedDimension} dispatch={dispatch} locked={changeIsLocked(choice) || generatedChoices.has(choiceId)} t={t} />);
  generatedChoices.add(choiceId);
};
const generateChoiceVariantValuesChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  useChips,
  choice,
  generatedChoices,
  selected,
  multipleElements,
  dispatch,
  t
}: {
  useChips: boolean;
  choice: ChartVariantValuesChoice<DataKey, DataRow>;
  generatedChoices: Set<DataKey>;
  selected: SelectionValue<DataKey> | undefined;
  multipleElements: ReactNode[];
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
}): void => {
  const {
    id: choiceId,
    type
  } = choice;
  if (!Array.isArray(selected)) {
    errorLocal(`generateChoiceVariantValuesChartChoiceElements ${choiceId} ${type} invalid selected`, {
      choice,
      selected
    });
    return;
  }
  multipleElements.push(<MultipleSelection<DataKey, DataRow> key={choiceId} type={type} choice={choice} useChips={useChips} selected={selected} dispatch={dispatch} locked={changeIsLocked(choice) || generatedChoices.has(choiceId)} t={t} />);
  generatedChoices.add(choiceId);
};
const getSingleAndmultipleChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  singleElements,
  multipleElements,
  togetherElements,
  choicesTogether
}: {
  choice: ChartAxisChoice<DataKey, DataRow>;
  singleElements: ReactNode[];
  multipleElements: ReactNode[];
  togetherElements: Map<AxisType, ReactNode[]>;
  choicesTogether: boolean;
}): {
  singleElements: ReactNode[];
  multipleElements: ReactNode[];
} => {
  if (choicesTogether) {
    const {
      axis
    } = choice;
    let elements = togetherElements.get(axis);
    if (elements == null) {
      elements = [];
      togetherElements.set(axis, elements);
    }
    return {
      singleElements: elements,
      multipleElements: elements
    };
  }
  return {
    singleElements,
    multipleElements
  };
};
export const generateChartChoiceElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  useChips,
  singleElements: singleElementsProp,
  multipleElements: multipleElementsProp,
  togetherElements,
  generatedChoices,
  choicesTogether,
  choiceId,
  choicesMap,
  transposedMetricsLists,
  selections,
  dispatch,
  t
}: {
  useChips: boolean;
  singleElements: ReactNode[];
  multipleElements: ReactNode[];
  togetherElements: Map<AxisType, ReactNode[]>;
  generatedChoices: Set<DataKey>;
  choicesTogether: boolean;
  choiceId: DataKey;
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>;
  transposedMetricsLists: Map<DataKey, Set<DataKey>>;
  selections: ChartSelections<DataKey>;
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
}): void => {
  const choice = choicesMap[choiceId] as ChartAxisChoice<DataKey, DataRow>;
  if (choice == null) {
    errorLocal(`generateChartChoiceElements choice [${choiceId}] not found`, {
      choicesMap
    });
    return;
  }
  const {
    id,
    type
  } = choice;
  const selected = selections[id];
  const {
    singleElements,
    multipleElements
  } = getSingleAndmultipleChartChoiceElements({
    choice,
    singleElements: singleElementsProp,
    multipleElements: multipleElementsProp,
    togetherElements,
    choicesTogether
  });
  switch (type) {
    case 'metric':
      generateChoiceMetricChartChoiceElements<DataKey, DataRow>({
        choice,
        choicesMap,
        generatedChoices,
        selections,
        selected,
        singleElements,
        dispatch
      });
      break;
    case 'metrics':
      generateChoiceMetricsChartChoiceElements<DataKey, DataRow>({
        useChips,
        choice,
        choicesMap,
        generatedChoices,
        selections,
        selected,
        singleElements,
        multipleElements,
        dispatch,
        t
      });
      break;
    case 'metricTransposed':
      generateChoiceMetricTransposedChartChoiceElements<DataKey, DataRow>({
        useChips,
        choice,
        choicesMap,
        generatedChoices,
        transposedMetricsLists,
        selections,
        selected,
        singleElements,
        multipleElements,
        dispatch,
        t
      });
      break;
    case 'metricVariants':
      generateChoiceMetricVariantsChartChoiceElements<DataKey, DataRow>({
        useChips,
        choice,
        choicesMap,
        generatedChoices,
        selections,
        selected,
        singleElements,
        multipleElements,
        dispatch,
        t
      });
      break;
    case 'values':
      generateChoiceValuesChartChoiceElements<DataKey, DataRow>({
        useChips,
        choice,
        generatedChoices,
        selected,
        multipleElements,
        dispatch,
        t
      });
      break;
    case 'valuesOf':
      generateChoiceValuesOfChartChoiceElements<DataKey, DataRow>({
        useChips,
        choice,
        choicesMap,
        generatedChoices,
        selections,
        selected,
        singleElements,
        multipleElements,
        dispatch,
        t
      });
      break;
    case 'variantValues':
      generateChoiceVariantValuesChartChoiceElements<DataKey, DataRow>({
        useChips,
        choice,
        generatedChoices,
        selected,
        multipleElements,
        dispatch,
        t
      });
      break;
    default:
      assertNever(type);
  }
};